import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineChevronLeft, HiOutlineRefresh, HiOutlineSearch, HiOutlinePlus, HiOutlineDownload, HiOutlineUpload } from 'react-icons/hi';
import { FaWhatsapp, FaEdit, FaTrash, FaMapMarkerAlt } from 'react-icons/fa';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { hasCardAction } from '../../utils/permissionHelper';
import DeliveryForm from './DeliveryForm';
import DashboardStats from './DashboardStats';
import MyOrders from './MyOrders';
import ImageViewer from './ImageViewer';
import ExportModal from './ExportModal';
import BulkUploadModal from './BulkUploadModal';
import ProofManageModal from './ProofManageModal';
import ProofPlaceholder from '../common/ProofPlaceholder';
import DeliveryAlertSettings from './DeliveryAlertSettings';
import DeliveryIndicators, { hasProof, STATUS_OPTIONS } from './DeliveryIndicators';
import './DeliveryManagement.css';

const DeliveryManagement = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    // Declared before the state below because activeTab is initialised from it.
    const isDeliveryBoy = user?.role === 'delivery_boy';
    const [deliveries, setDeliveries] = useState([]);
    const [deliveryBoys, setDeliveryBoys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingDelivery, setEditingDelivery] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    // The term actually sent to the server, 500ms behind what is being typed.
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [viewingImage, setViewingImage] = useState(null);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [managingProofFor, setManagingProofFor] = useState(null);
    const [remarksInputs, setRemarksInputs] = useState({}); // Draft remarks per delivery, before Save
    const [savingRemarks, setSavingRemarks] = useState({}); // deliveryId -> true while the save is in flight
    const [filters, setFilters] = useState({
        status: '',
        // Independent of `status` — all three are sent together and all three
        // narrow. '' means "don't filter" and is stripped before the request.
        hasProof: '',
        invoicePending: '',
        paymentStatus: '',
        deliveryBoy: '',
        area: '',
        dateFrom: '',
        dateTo: '',
        company: '',
        branch: ''
    });
    // Admin-only tenant scope: list of companies and the branches of the picked company
    const [companies, setCompanies] = useState([]);
    const [branches, setBranches] = useState([]);
    // 'list', 'dashboard', or 'my-orders'. Delivery boys start on their own tab
    // rather than defaulting to 'list' and being corrected by an effect: the
    // correction landed one render too late, so the fetch effects below had
    // already run against 'list' and fired a full admin list query that a driver
    // never sees. ProtectedRoute won't mount this without a loaded `user`, so the
    // role is known on the very first render.
    const [activeTab, setActiveTab] = useState(isDeliveryBoy ? 'my-orders' : 'list');
    
    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    const userPermissions = user?.permissions || { cards: [], actions: [] };
    // Only a system_admin sees all branches and may narrow the view to one
    // company/branch. Normal users are auto-scoped server-side, so no UI for them.
    const isSystemAdmin = user?.role === 'system_admin';
    const canCreate = user?.role === 'system_admin' || hasCardAction(userPermissions, 'delivery-management', 'create');
    const canEdit = user?.role === 'system_admin' || hasCardAction(userPermissions, 'delivery-management', 'edit');
    const canDelete = user?.role === 'system_admin' || hasCardAction(userPermissions, 'delivery-management', 'delete');

    // "Head Office → Wholesale Market" for the admin's current scope, so a
    // write screen can state where the record is about to be filed instead of
    // making them trust the dropdowns behind the modal. Empty until both are
    // picked — a company alone is not a write target.
    const selectedScopeLabel = (() => {
        if (!isSystemAdmin || !filters.company || !filters.branch) return '';
        const companyName = companies.find(c => c._id === filters.company)?.name || '';
        const branchName = branches.find(b => b._id === filters.branch)?.name || '';
        return companyName && branchName ? `${companyName} → ${branchName}` : '';
    })();

    // Fetch delivery boys list. Only feeds the "All Delivery Boys" filter and the
    // bulk-upload modal, both of which live on the admin list tab — a driver never
    // renders either, so don't spend the request on them.
    useEffect(() => {
        if (isDeliveryBoy) return;
        const fetchDeliveryBoys = async () => {
            try {
                const response = await axiosInstance.get('/api/deliveries/delivery-boys');
                if (response.data.success) {
                    setDeliveryBoys(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching delivery boys:', error);
            }
        };
        fetchDeliveryBoys();
    }, [isDeliveryBoy]);

    // Admin only: load the list of companies once for the scope filter.
    useEffect(() => {
        if (!isSystemAdmin) return;
        const fetchCompanies = async () => {
            try {
                const response = await axiosInstance.get('/api/tenants');
                if (response.data.success) {
                    setCompanies(response.data.tenants || []);
                }
            } catch (error) {
                console.error('Error fetching companies:', error);
            }
        };
        fetchCompanies();
    }, [isSystemAdmin]);

    // Admin only: when a company is picked, load its branches. Clearing the
    // company empties the branch list.
    useEffect(() => {
        if (!isSystemAdmin) return;
        if (!filters.company) {
            setBranches([]);
            return;
        }
        const fetchBranches = async () => {
            try {
                const response = await axiosInstance.get('/api/tenants', {
                    params: { company: filters.company }
                });
                if (response.data.success) {
                    setBranches(response.data.tenants || []);
                }
            } catch (error) {
                console.error('Error fetching branches:', error);
            }
        };
        fetchBranches();
    }, [filters.company, isSystemAdmin]);

    // Picking a company resets the branch and jumps back to page 1.
    const handleCompanyChange = (e) => {
        setFilters(prev => ({ ...prev, company: e.target.value, branch: '' }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleBranchChange = (e) => {
        setFilters(prev => ({ ...prev, branch: e.target.value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleClearScope = () => {
        setFilters(prev => ({ ...prev, company: '', branch: '' }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Rendered on BOTH the Deliveries list and the Dashboard. The picker used to
    // live only inside the list, so an admin who narrowed to one branch and
    // flipped to Dashboard had no way to see or change which branch those totals
    // covered — while the totals themselves were quietly following the pick.
    const renderAdminScope = () => {
        if (!isSystemAdmin) return null;
        return (
            <div className="dm-admin-scope">
                <span className="dm-admin-scope-label">🏢 Viewing data for:</span>
                <select
                    value={filters.company}
                    onChange={handleCompanyChange}
                    className="dm-filter-select"
                >
                    <option value="">All Companies</option>
                    {companies.map((c) => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                </select>
                <select
                    value={filters.branch}
                    onChange={handleBranchChange}
                    className="dm-filter-select"
                    disabled={!filters.company}
                >
                    <option value="">
                        {filters.company ? 'All Branches' : 'Select company first'}
                    </option>
                    {branches.map((b) => (
                        <option key={b._id} value={b._id}>{b.name}</option>
                    ))}
                </select>
                {(filters.company || filters.branch) && (
                    <button onClick={handleClearScope} className="btn btn-secondary btn-sm">
                        Clear
                    </button>
                )}
            </div>
        );
    };

    // Typing waits 500ms before it reaches the server, and a new search restarts
    // at page 1. Stored trimmed so adding a trailing space isn't a new query.
    useEffect(() => {
        const delaySearch = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
            setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
        }, 500);
        return () => clearTimeout(delaySearch);
    }, [searchTerm]);

    // The ONE place the list is loaded. This used to be two effects — a debounced
    // one and an immediate one for paging — and on mount both fired, so every
    // visit to the tab fetched the same list twice, 500ms apart.
    useEffect(() => {
        if (activeTab !== 'list') return;
        fetchDeliveries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, filters, activeTab, pagination.page, pagination.limit]);

    const fetchDeliveries = async () => {
        try {
            setLoading(true);
            // debouncedSearch, not searchTerm: the request must carry the same term
            // that triggered it, or a filter change mid-typing fires one request now
            // and an identical one when the debounce settles.
            const params = {
                search: debouncedSearch,
                ...filters,
                page: pagination.page,
                limit: pagination.limit
            };
            // Remove empty params
            Object.keys(params).forEach(key => !params[key] && delete params[key]);

            const response = await axiosInstance.get('/api/deliveries', { params });
            if (response.data.success) {
                setDeliveries(response.data.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.total || 0,
                    pages: response.data.pages || 0
                }));
            }
        } catch (error) {
            console.error('Error fetching deliveries:', error);
            alert('Failed to fetch deliveries');
        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = () => {
        setEditingDelivery(null);
        setShowForm(true);
    };

    const handleEdit = (delivery) => {
        setEditingDelivery(delivery);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this delivery?')) return;

        try {
            await axiosInstance.delete(`/api/deliveries/${id}`);
            alert('Delivery deleted successfully');
            fetchDeliveries();
        } catch (error) {
            console.error('Error deleting delivery:', error);
            alert('Failed to delete delivery');
        }
    };

    const handleFormClose = (refresh) => {
        setShowForm(false);
        setEditingDelivery(null);
        if (refresh) fetchDeliveries();
    };

    const handleStatusChange = async (delivery, newStatus) => {
        try {
            await axiosInstance.put(`/api/deliveries/${delivery._id}`, { status: newStatus });
            fetchDeliveries();
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update status');
        }
    };

    const handleRemarksChange = (deliveryId, value) => {
        setRemarksInputs(prev => ({ ...prev, [deliveryId]: value }));
    };

    // Overwrites the single `remarks` field right from the row — no need to
    // open the full edit form just to leave a note. Backend stamps who/when.
    const handleSaveRemarks = async (delivery) => {
        const text = (remarksInputs[delivery._id] ?? delivery.remarks ?? '').trim();
        setSavingRemarks(prev => ({ ...prev, [delivery._id]: true }));
        try {
            await axiosInstance.put(`/api/deliveries/${delivery._id}`, { remarks: text });
            setRemarksInputs(prev => {
                const updated = { ...prev };
                delete updated[delivery._id];
                return updated;
            });
            fetchDeliveries();
        } catch (error) {
            console.error('Error saving remarks:', error);
            alert(error.response?.data?.message || 'Failed to save remarks');
        } finally {
            setSavingRemarks(prev => ({ ...prev, [delivery._id]: false }));
        }
    };

    // Legacy single proof photo. Fetched ONLY on click — the tile that triggers
    // this is a placeholder, so a list of 20 rows costs no image bandwidth until
    // someone actually asks to see one.
    const handleViewLegacyProof = async (delivery) => {
        try {
            const response = await axiosInstance.get(
                `/api/deliveries/${delivery._id}/proof`,
                { responseType: 'blob' }
            );
            const url = URL.createObjectURL(response.data);
            setViewingImage({ url, title: `Proof - ${delivery.invoiceNo}`, revoke: true });
        } catch (error) {
            console.error('Error loading proof image:', error);
            alert('Failed to load proof image');
        }
    };

    const handleWhatsAppClick = (delivery) => {
        // Mobile is already in 8-digit format, just add 974 prefix for WhatsApp
        const formattedPhone = `974${delivery.mobile}`;
        
        // Google review link
        const reviewUrl = 'https://g.page/r/CWWiBDkVlmRWEAE/review';
        
        // WhatsApp message template with bold formatting
        const message = `*━━━━━━━━━━━━━━━━━━━━*
*GREETINGS FROM AHLA MART*
*━━━━━━━━━━━━━━━━━━━━*

Thank you for your order with us!

*ORDER DETAILS*
*Invoice Number:* ${delivery.invoiceNo}
*Customer Name:* ${delivery.customerName}

*━━━━━━━━━━━━━━━━━━━━*

*WE VALUE YOUR FEEDBACK!*

Share your experience on Google Reviews:

${reviewUrl}

*━━━━━━━━━━━━━━━━━━━━*
*THANK YOU FOR CHOOSING AHLA MART!*
*━━━━━━━━━━━━━━━━━━━━*`;
        
        // Open WhatsApp with pre-filled message
        const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    // Status colour lives in DeliveryIndicators now, so both this screen and
    // the driver's card render a delivery's state identically.

    // Mirrors the `paymentStatus` virtual on the Delivery model. The API already
    // sends it, but recompute as a fallback so the badge never renders blank.
    const getPaymentStatus = (delivery) => {
        if (delivery.paymentStatus) return delivery.paymentStatus;
        const paid = delivery.amountPaid || 0;
        if (paid === 0) return 'Unpaid';
        if (paid >= (delivery.amount || 0)) return 'Paid';
        return 'Partial';
    };

    const getPaymentBadgeClass = (paymentStatus) => {
        switch (paymentStatus) {
            case 'Paid': return 'dm-pay-paid';
            case 'Partial': return 'dm-pay-partial';
            default: return 'dm-pay-unpaid';
        }
    };

    // Which date the row's timestamp shows, chosen by where the delivery is.
    // Proof and invoice-pending have their own timestamps (proofUploadedAt,
    // invoicePendingAt) and no longer compete for this one.
    const getStatusTimestamp = (delivery) => {
        switch (delivery.status) {
            case 'Pending':
                return delivery.createdAt;
            case 'In Transit':
                return delivery.startedAt;
            case 'Delivered':
                return delivery.deliveredAt;
            case 'Failed':
                return delivery.proofUploadedAt || delivery.createdAt;
            default:
                return delivery.createdAt;
        }
    };

    const formatStatusTimestamp = (timestamp) => {
        if (!timestamp) return null;
        return new Date(timestamp).toLocaleString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    // Row content shared verbatim between the desktop <table> and the
    // <=600px card list — same handlers/state, just two different wrappers
    // (<td> vs a labeled card section) around the same JSX, so mobile can
    // never drift out of sync with desktop or skip a field.
    const renderDeliveryActions = (delivery) => (
        <div className="dm-action-buttons-vertical">
            {delivery.status === 'Delivered' && (
                <button
                    onClick={() => handleWhatsAppClick(delivery)}
                    className="dm-action-icon dm-whatsapp-icon"
                    title="Send feedback request via WhatsApp"
                >
                    <FaWhatsapp size={18} />
                </button>
            )}
            {canEdit && (
                <button
                    onClick={() => handleEdit(delivery)}
                    className="dm-action-icon dm-edit-icon"
                    title="Edit delivery"
                >
                    <FaEdit size={16} />
                </button>
            )}
            {canDelete && (
                <button
                    onClick={() => handleDelete(delivery._id)}
                    className="dm-action-icon dm-delete-icon"
                    title="Delete delivery"
                >
                    <FaTrash size={16} />
                </button>
            )}
        </div>
    );

    const renderCustomerInfo = (delivery) => (
        <div className="dm-customer-info">
            <div className="dm-customer-name">{delivery.customerName}</div>
            <div className="dm-customer-mobile">📱 {delivery.mobile}</div>
            <div className="dm-customer-location">
                {delivery.area && (
                    <span className="dm-location-text">📍 {delivery.area}</span>
                )}
                {delivery.status === 'In Transit' && delivery.liveLocationUrl ? (
                    <a
                        href={delivery.liveLocationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dm-live-location-btn"
                        title="Track Live Location"
                    >
                        <FaMapMarkerAlt size={12} className="pulse-icon" />
                        <span>Live</span>
                    </a>
                ) : delivery.googleMapsUrl ? (
                    <a
                        href={delivery.googleMapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dm-location-btn"
                        title="Open in Google Maps"
                    >
                        <FaMapMarkerAlt size={12} />
                        <span>Map</span>
                    </a>
                ) : null}
            </div>
            {delivery.customerId?.salesman && (
                <div className="dm-location-text" title="Salesman">
                    🧑‍💼 {delivery.customerId.salesman.name} ({delivery.customerId.salesman.employeeId})
                </div>
            )}
        </div>
    );

    const renderAmountInfo = (delivery) => (
        <div className="dm-amount-cell">
            <div className="dm-amount-total">
                <strong>QAR {delivery.amount.toLocaleString()}</strong>
            </div>
            {/* Paid / Partial / Unpaid at a glance — shown on every row
                so the payment filter's result is readable for
                Pending and In Transit orders too. */}
            <div className={`dm-payment-status-badge ${getPaymentBadgeClass(getPaymentStatus(delivery))}`}>
                {getPaymentStatus(delivery)}
            </div>
            {(hasProof(delivery) || delivery.status === 'Delivered') && (
                <>
                    <div className="dm-payment-type-badge">
                        💳 {delivery.paymentType || 'COD'}
                    </div>
                    <div className="dm-amount-paid">
                        Paid: <span className="dm-paid-value">QAR {(delivery.amountPaid || 0).toLocaleString()}</span>
                    </div>
                    <div className={`dm-amount-balance ${((delivery.amount || 0) - (delivery.amountPaid || 0)) === 0 ? 'dm-balance-zero' : 'dm-balance-pending'}`}>
                        Balance: <span className="dm-balance-value">QAR {((delivery.amount || 0) - (delivery.amountPaid || 0)).toLocaleString()}</span>
                    </div>
                </>
            )}
        </div>
    );

    const renderStatusInfo = (delivery) => (
        <div className="dm-status-cell">
            <div className="dm-delivery-boy-name">
                👤 {delivery.deliveryBoy?.fullName || delivery.deliveryBoy?.username || delivery.deliveryBoy || '-'}
            </div>
            {delivery.helpers && (
                <div className="dm-helpers" title="Helpers">
                    🤝 {delivery.helpers}
                </div>
            )}
            {canEdit ? (
                <>
                    <select
                        value={delivery.status}
                        onChange={(e) => handleStatusChange(delivery, e.target.value)}
                        className="dm-status-select"
                    >
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <div className="dm-indicator-row">
                        <DeliveryIndicators delivery={delivery} />
                    </div>
                </>
            ) : (
                <DeliveryIndicators delivery={delivery} />
            )}
            {formatStatusTimestamp(getStatusTimestamp(delivery)) && (
                <small className="dm-status-timestamp">
                    📅 {formatStatusTimestamp(getStatusTimestamp(delivery))}
                </small>
            )}
        </div>
    );

    // Inline-editable right in the row — no need to open the full edit form
    // just to leave a note. Overwrites the single `remarks` field (not a
    // thread), so the attribution line shows only the LAST person to save.
    const renderRemarks = (delivery) => {
        if (!canEdit) {
            return (
                <>
                    <span className="dm-remarks">{delivery.remarks || '-'}</span>
                    {delivery.remarksUpdatedBy && (
                        <small className="dm-remarks-attribution">
                            — {delivery.remarksUpdatedBy}
                            {delivery.remarksUpdatedAt && ` · ${new Date(delivery.remarksUpdatedAt).toLocaleString('en-US', {
                                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}`}
                        </small>
                    )}
                </>
            );
        }

        const draft = remarksInputs[delivery._id] ?? delivery.remarks ?? '';
        const dirty = draft !== (delivery.remarks || '');
        return (
            <div className="dm-remarks-inline">
                <input
                    type="text"
                    className="dm-remarks-input"
                    placeholder="Add a remark..."
                    value={draft}
                    onChange={(e) => handleRemarksChange(delivery._id, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveRemarks(delivery); }}
                />
                {dirty && (
                    <button
                        type="button"
                        className="dm-remarks-save-btn"
                        disabled={savingRemarks[delivery._id]}
                        onClick={() => handleSaveRemarks(delivery)}
                        title="Save remark"
                    >
                        {savingRemarks[delivery._id] ? '...' : '✓'}
                    </button>
                )}
                {delivery.remarksUpdatedBy && (
                    <small className="dm-remarks-attribution">
                        — {delivery.remarksUpdatedBy}
                        {delivery.remarksUpdatedAt && ` · ${new Date(delivery.remarksUpdatedAt).toLocaleString('en-US', {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}`}
                    </small>
                )}
            </div>
        );
    };

    const renderProofInfo = (delivery) => (
        <div className="dm-proof-combined">
            {delivery.proofDocuments?.length > 0 ? (
                <div className="dm-proof-doc-badge" onClick={() => setManagingProofFor(delivery)} title="Manage proof documents">
                    {delivery.proofDocuments[0].fileType?.startsWith('image/') ? (
                        <ProofPlaceholder
                            className="dm-proof-thumbnail"
                            title="Open proof documents"
                        />
                    ) : (
                        <div className="dm-proof-thumbnail dm-proof-doc-icon">📄</div>
                    )}
                    {delivery.proofDocuments.length > 1 && (
                        <span className="dm-proof-doc-count">+{delivery.proofDocuments.length - 1}</span>
                    )}
                </div>
            ) : (delivery.proofPhoto || delivery.proofStorage === 'sharepoint') ? (
                <ProofPlaceholder
                    className="dm-proof-thumbnail"
                    onClick={() => handleViewLegacyProof(delivery)}
                    title="Click to view full image"
                />
            ) : (
                <span className="dm-no-proof">-</span>
            )}
            {delivery.proofNote && (
                <div className="dm-proof-note-wrapper">
                    <span
                        className={`dm-proof-note ${delivery.status === 'Failed' ? 'dm-proof-note-failed' : ''}`}
                        title={delivery.proofNote}
                    >
                        {delivery.proofNote.length > 50
                            ? `${delivery.proofNote.substring(0, 50)}...`
                            : delivery.proofNote}
                    </span>
                    {delivery.proofUploadedAt && (
                        <small className="dm-proof-note-time">
                            📅 {new Date(delivery.proofUploadedAt).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </small>
                    )}
                </div>
            )}
            {canEdit && (
                <button
                    type="button"
                    className="dm-manage-proof-btn"
                    onClick={() => setManagingProofFor(delivery)}
                >
                    Manage
                </button>
            )}
        </div>
    );

    if (loading && deliveries.length === 0 && activeTab === 'list') {
        return (
            <div className="dm-shell">
                <div className="dm-loading"><div className="spinner"></div>Loading deliveries...</div>
            </div>
        );
    }

    return (
        <div className="dm-shell">
            {/* HEADER */}
            <header className="dm-header">
                <div className="dm-header-left">
                    <button className="dm-back-btn" onClick={() => navigate('/purpose-selection')}>
                        <HiOutlineChevronLeft size={20} />
                    </button>
                    <div className="dm-header-title">
                        <h1>Delivery Management</h1>
                        <span>Module</span>
                    </div>
                </div>
                <div className="dm-header-right">
                    <div className="dm-tabs">
                        {!isDeliveryBoy && (
                            <>
                                <button 
                                    className={`dm-tab ${activeTab === 'list' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('list')}
                                >
                                    Deliveries
                                </button>
                                <button
                                    className={`dm-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('dashboard')}
                                >
                                    Dashboard
                                </button>
                            </>
                        )}
                        {isSystemAdmin && (
                            <button
                                className={`dm-tab ${activeTab === 'alerts' ? 'active' : ''}`}
                                onClick={() => setActiveTab('alerts')}
                            >
                                Alerts
                            </button>
                        )}
                        <button 
                            className={`dm-tab ${activeTab === 'my-orders' ? 'active' : ''}`}
                            onClick={() => setActiveTab('my-orders')}
                        >
                            {isDeliveryBoy ? 'My Orders' : 'My Deliveries'}
                        </button>
                    </div>
                    {activeTab === 'list' && (
                        <>
                            {canCreate && (
                                <button className="btn btn-primary btn-sm" onClick={handleAddNew}>
                                    <HiOutlinePlus /> Add Delivery
                                </button>
                            )}
                            {canCreate && (
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setShowBulkUploadModal(true)}
                                >
                                    <HiOutlineUpload /> Bulk Upload
                                </button>
                            )}
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setShowExportModal(true)}
                            >
                                <HiOutlineDownload /> Export Report
                            </button>
                            <button 
                                className="btn btn-secondary btn-sm" 
                                onClick={fetchDeliveries}
                                disabled={loading}
                            >
                                <HiOutlineRefresh /> {loading ? 'Refreshing...' : 'Refresh'}
                            </button>
                        </>
                    )}
                </div>
            </header>

            {/* BODY */}
            <div className="dm-body">
                {activeTab === 'my-orders' ? (
                    <MyOrders
                        isDeliveryBoy={isDeliveryBoy}
                        // System-admin company/branch picker, same one the
                        // Deliveries and Dashboard tabs render. Null for
                        // everyone else, who is auto-scoped server-side.
                        scopeSelector={renderAdminScope()}
                        scope={isSystemAdmin ? { company: filters.company, branch: filters.branch } : null}
                    />
                ) : activeTab === 'alerts' ? (
                    <DeliveryAlertSettings />
                ) : activeTab === 'dashboard' ? (
                    <div className="dm-dashboard-wrap">
                        {renderAdminScope()}
                        <DashboardStats
                            scope={isSystemAdmin ? { company: filters.company, branch: filters.branch } : null}
                        />
                    </div>
                ) : (
                    <main className="dm-content">
                        {/* Search & Filters */}
                        <div className="dm-filters-section">
                            {renderAdminScope()}
                            <div className="dm-search-wrapper">
                                <HiOutlineSearch className="dm-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search by customer, mobile, invoice, or remarks..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="dm-search-input"
                                />
                            </div>
                            <div className="dm-filter-row">
                                <select
                                    value={filters.status}
                                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                                    className="dm-filter-select"
                                >
                                    <option value="">All Status</option>
                                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>

                                {/* Independent of the status above — all three compose. */}
                                <select
                                    value={filters.hasProof}
                                    onChange={(e) => {
                                        setFilters({...filters, hasProof: e.target.value});
                                        setPagination(prev => ({ ...prev, page: 1 }));
                                    }}
                                    className="dm-filter-select"
                                >
                                    <option value="">Proof: Any</option>
                                    <option value="true">📷 Has proof</option>
                                    <option value="false">No proof</option>
                                </select>

                                <select
                                    value={filters.invoicePending}
                                    onChange={(e) => {
                                        setFilters({...filters, invoicePending: e.target.value});
                                        setPagination(prev => ({ ...prev, page: 1 }));
                                    }}
                                    className="dm-filter-select"
                                >
                                    <option value="">Invoice: Any</option>
                                    <option value="true">🧾 Invoice pending</option>
                                    <option value="false">Invoice raised</option>
                                </select>
                                <select
                                    value={filters.paymentStatus}
                                    onChange={(e) => {
                                        setFilters({...filters, paymentStatus: e.target.value});
                                        setPagination(prev => ({ ...prev, page: 1 }));
                                    }}
                                    className="dm-filter-select"
                                >
                                    <option value="">All Payments</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Partial">Partially Paid</option>
                                    <option value="Unpaid">Unpaid</option>
                                    <option value="NotPaid">Not Fully Paid (Unpaid + Partial)</option>
                                </select>
                                <select
                                    value={filters.deliveryBoy}
                                    onChange={(e) => setFilters({...filters, deliveryBoy: e.target.value})}
                                    className="dm-filter-select"
                                >
                                    <option value="">All Delivery Boys</option>
                                    {deliveryBoys.map((boy) => (
                                        <option key={boy._id} value={boy._id}>
                                            {boy.fullName || boy.username}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    placeholder="Filter by area"
                                    value={filters.area}
                                    onChange={(e) => setFilters({...filters, area: e.target.value})}
                                    className="dm-filter-input"
                                />
                            </div>
                            <div className="dm-filter-row">
                                <div className="dm-date-filter">
                                    <label>
                                        {filters.status === 'Delivered' ? 'Delivered Date From:' :
                                         filters.status === 'In Transit' ? 'Started Date From:' :
                                         'Created Date From:'}
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.dateFrom}
                                        onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                                        className="dm-filter-input"
                                    />
                                </div>
                                <div className="dm-date-filter">
                                    <label>
                                        {filters.status === 'Delivered' ? 'Delivered Date To:' :
                                         filters.status === 'In Transit' ? 'Started Date To:' :
                                         'Created Date To:'}
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.dateTo}
                                        onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                                        className="dm-filter-input"
                                    />
                                </div>
                                {(filters.dateFrom || filters.dateTo) && (
                                    <button
                                        onClick={() => setFilters({...filters, dateFrom: '', dateTo: ''})}
                                        className="btn btn-secondary btn-sm"
                                        style={{ alignSelf: 'flex-end' }}
                                    >
                                        Clear Dates
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Results Info */}
                        <div className="dm-results-info">
                            <span>
                                Showing {deliveries.length > 0 ? ((pagination.page - 1) * pagination.limit + 1) : 0} 
                                {' '}-{' '}
                                {Math.min(pagination.page * pagination.limit, pagination.total)} 
                                {' '}of {pagination.total} deliveries
                            </span>
                            <div className="dm-page-size">
                                <label>Items per page:</label>
                                <select 
                                    value={pagination.limit} 
                                    onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                                    className="dm-page-size-select"
                                >
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                        </div>

                        {/* Table */}
                        {/* Desktop/tablet: unchanged table, hidden <=600px via CSS (see
                            DeliveryManagement.css) rather than a JS breakpoint check —
                            both this and .dm-mobile-cards read the same `deliveries`
                            array and the same render* helpers above, so there is no
                            separate mobile data path to fall out of sync. */}
                        <div className="dm-table-wrap">
                            <div className="dm-table-container">
                                <table className="dm-table">
                                    <thead>
                                        <tr>
                                            <th>Actions</th>
                                            <th>Invoice No</th>
                                            <th>Customer</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th>Proof</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {deliveries.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="dm-empty-table">
                                                    No deliveries found
                                                </td>
                                            </tr>
                                        ) : (
                                            deliveries.map((delivery) => (
                                                <tr key={delivery._id}>
                                                    <td>{renderDeliveryActions(delivery)}</td>
                                                    <td><strong>{delivery.invoiceNo}</strong></td>
                                                    <td>{renderCustomerInfo(delivery)}</td>
                                                    <td>{renderAmountInfo(delivery)}</td>
                                                    <td>{renderStatusInfo(delivery)}</td>
                                                    <td>{renderProofInfo(delivery)}</td>
                                                    <td>
                                                        {renderRemarks(delivery)}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Mobile (<=600px): same data, same handlers, card layout
                            instead of a table. Rendered always and toggled with the
                            table via CSS (display:none in the matching media query)
                            so there is exactly one place that decides mobile-vs-desktop —
                            the stylesheet — instead of a resize listener plus render branch. */}
                        <div className="dm-mobile-cards">
                            {deliveries.length === 0 ? (
                                <div className="dm-empty-table">No deliveries found</div>
                            ) : (
                                deliveries.map((delivery) => (
                                    <div className="dm-mobile-card" key={delivery._id}>
                                        <div className="dm-mobile-card-header">
                                            <div className="dm-mobile-card-invoice">
                                                <span className="dm-mobile-card-label">Invoice</span>
                                                <strong>{delivery.invoiceNo}</strong>
                                            </div>
                                            {renderDeliveryActions(delivery)}
                                        </div>

                                        <div className="dm-mobile-card-section">
                                            {renderCustomerInfo(delivery)}
                                        </div>

                                        <div className="dm-mobile-card-section">
                                            <span className="dm-mobile-card-label">Amount</span>
                                            {renderAmountInfo(delivery)}
                                        </div>

                                        <div className="dm-mobile-card-section">
                                            <span className="dm-mobile-card-label">Status</span>
                                            {renderStatusInfo(delivery)}
                                        </div>

                                        <div className="dm-mobile-card-section">
                                            <span className="dm-mobile-card-label">Proof</span>
                                            {renderProofInfo(delivery)}
                                        </div>

                                        {(delivery.remarks || canEdit) && (
                                            <div className="dm-mobile-card-section">
                                                <span className="dm-mobile-card-label">Remarks</span>
                                                {renderRemarks(delivery)}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <div className="dm-pagination">
                                <button 
                                    className="dm-page-btn"
                                    onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
                                    disabled={pagination.page === 1}
                                >
                                    First
                                </button>
                                <button 
                                    className="dm-page-btn"
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                    disabled={pagination.page === 1}
                                >
                                    Previous
                                </button>
                                
                                <div className="dm-page-numbers">
                                    {[...Array(pagination.pages)].map((_, idx) => {
                                        const pageNum = idx + 1;
                                        // Show first, last, current, and 2 pages around current
                                        if (
                                            pageNum === 1 || 
                                            pageNum === pagination.pages ||
                                            (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`dm-page-num ${pagination.page === pageNum ? 'active' : ''}`}
                                                    onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        } else if (
                                            pageNum === pagination.page - 3 || 
                                            pageNum === pagination.page + 3
                                        ) {
                                            return <span key={pageNum} className="dm-page-ellipsis">...</span>;
                                        }
                                        return null;
                                    })}
                                </div>

                                <button 
                                    className="dm-page-btn"
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={pagination.page === pagination.pages}
                                >
                                    Next
                                </button>
                                <button 
                                    className="dm-page-btn"
                                    onClick={() => setPagination(prev => ({ ...prev, page: pagination.pages }))}
                                    disabled={pagination.page === pagination.pages}
                                >
                                    Last
                                </button>
                            </div>
                        )}
                    </main>
                )}
            </div>

            {showForm && (
                <DeliveryForm
                    delivery={editingDelivery}
                    onClose={handleFormClose}
                    // The "Viewing data for" scope doubles as the WRITE target
                    // for an admin: whichever branch they are looking at is the
                    // branch a new delivery is filed into. Empty for a normal
                    // user, whose branch the server fills in.
                    scope={isSystemAdmin ? { company: filters.company, branch: filters.branch } : null}
                />
            )}

            {viewingImage && (
                <ImageViewer
                    imageUrl={viewingImage.url}
                    title={viewingImage.title}
                    onClose={() => {
                        // The blob was created for this viewing only (no thumbnail
                        // holds a reference any more), so release it on close.
                        if (viewingImage.revoke) URL.revokeObjectURL(viewingImage.url);
                        setViewingImage(null);
                    }}
                />
            )}

            {showExportModal && (
                <ExportModal
                    onClose={() => setShowExportModal(false)}
                    currentFilters={filters}
                />
            )}

            {showBulkUploadModal && (
                <BulkUploadModal
                    deliveryBoys={deliveryBoys}
                    onClose={() => setShowBulkUploadModal(false)}
                    onUploaded={fetchDeliveries}
                    scope={isSystemAdmin ? { company: filters.company, branch: filters.branch } : null}
                    scopeLabel={selectedScopeLabel}
                />
            )}

            {managingProofFor && (
                <ProofManageModal
                    delivery={managingProofFor}
                    onClose={() => setManagingProofFor(null)}
                    onUpdated={(updatedDocs) => {
                        setDeliveries(prev => prev.map(d =>
                            d._id === managingProofFor._id ? { ...d, proofDocuments: updatedDocs } : d
                        ));
                        setManagingProofFor(prev => prev && { ...prev, proofDocuments: updatedDocs });
                    }}
                />
            )}
        </div>
    );
};

export default DeliveryManagement;
